// public/js/features/assets/stand-model-panel.js
// The 3D model panel on the asset detail page.
//
// Self-gating, like the tracker-config and derived-sensor panels: it renders
// nothing unless the asset carries an `engineStandType` we actually hold a model
// for. No type, no model, no panel — and no empty heading either.
//
// The viewer is <model-viewer>, self-hosted from the static bucket (see
// scripts/vendor-sync.sh). It is a custom element, so the only integration is
// the tag plus attributes; camera controls, lighting and the loading state are
// its own.
//
// It is loaded LAZILY, on the first asset that actually has a model: the module
// is 956 KB, and every other page in the app would pay for it on load otherwise.
// One dynamic import, cached by the browser afterwards.

import { hasStandModel, standModel } from './stand-models.js';

const VIEWER_SRC = '/vendor/model-viewer-4.0.0.min.js';
let viewerLoading = null;

// Load the custom element once, then wait for it to be defined. Resolving on
// customElements.whenDefined (not just the import) means the first render never
// races the upgrade and shows an unstyled empty box.
function loadViewer() {
  if (window.customElements && window.customElements.get('model-viewer')) return Promise.resolve();
  if (!viewerLoading) {
    const base = (typeof window !== 'undefined' && window.STATIC_BASE) || '';
    viewerLoading = import(/* @vite-ignore */ base + VIEWER_SRC)
      .then(() => window.customElements.whenDefined('model-viewer'))
      .catch(err => { viewerLoading = null; throw err; });
  }
  return viewerLoading;
}

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

/**
 * Mount the panel into `container` for `asset`.
 * Returns true when a viewer was rendered, false when there is nothing to show.
 */
export async function renderStandModelPanel(container, asset) {
  if (!container) return false;
  const standType = asset && asset.engineStandType;
  if (!hasStandModel(standType)) { container.innerHTML = ''; return false; }

  const meta = standModel(standType);

  // The signed URL is minted per view and expires; we fetch it first so a failure
  // leaves the panel out entirely rather than showing a broken viewer.
  let src;
  try {
    const res = await window.authenticatedFetch(
      `api/v1/asset-models/${encodeURIComponent(standType)}/url`, {});
    if (!res.ok) throw new Error(`model url ${res.status}`);
    src = (await res.json()).url;
  } catch (e) {
    console.warn('[stand-model] no model URL for', standType, e);
    container.innerHTML = '';
    return false;
  }

  // A failed LIBRARY load is different from "no model": the asset does have a
  // model, we just cannot draw it. Say so instead of leaving a blank space —
  // silence here cost an afternoon of looking for a panel that had removed
  // itself over a missing CORS header on the vendor bucket.
  try {
    await loadViewer();
  } catch (e) {
    console.warn('[stand-model] viewer library failed to load', e);
    container.innerHTML = `
      <h5 class="mt-5 mb-3">3D model</h5>
      <div class="alert alert-warning py-2 px-3 small mb-4" role="status">
        The 3D viewer could not be loaded. Reload the page; if it persists the
        viewer library is not reachable from this browser.
      </div>`;
    return false;
  }

  container.innerHTML = `
    <h5 class="mt-5 mb-3 d-flex align-items-center gap-2 flex-wrap">
      3D model
      <span class="badge-soft-gray">${esc(meta.label || standType)}</span>
      <button type="button" class="btn btn-sm btn-outline-secondary ms-auto" data-stand-model-reset
              title="Reset the view">
        <i class="fa-solid fa-arrows-rotate me-1"></i> Reset view
      </button>
    </h5>
    <div class="card shadow-sm mb-4">
      <div class="card-body p-0">
        <model-viewer
          id="stand-model-viewer"
          src="${esc(src)}"
          alt="3D model of a ${esc(meta.label || standType)}"
          camera-controls
          touch-action="pan-y"
          auto-rotate
          auto-rotate-delay="3000"
          shadow-intensity="1.4"
          shadow-softness="0.75"
          exposure="0.95"
          environment-image="neutral"
          loading="eager"
          class="stand-model-stage">
          <div slot="progress-bar"></div>
        </model-viewer>
      </div>
    </div>`;

  const viewer = container.querySelector('#stand-model-viewer');
  const reset  = container.querySelector('[data-stand-model-reset]');
  if (reset && viewer) {
    reset.addEventListener('click', () => {
      // resetTurntableRotation + a fresh framing is what "reset" means to a user
      // who has spun the model into a corner.
      try { viewer.resetTurntableRotation(); viewer.cameraOrbit = 'auto auto auto'; } catch {}
    });
  }
  if (viewer) {
    viewer.addEventListener('error', (ev) => {
      console.warn('[stand-model] viewer error', ev && ev.detail);
      container.innerHTML = '';
    });
  }
  return true;
}
